import type { RetrievalProgressStateV2, TechFeatureType, ImageToTextResult } from '@/domains/novelty-check-report/models/retrieval-progress-model-v2';

export interface SearchNoveltyResultRequest {
    /**
     * 任务ID
     */
    task_id: string;
    /**
     * 是否完成
     */
    is_finished: boolean;
}

export interface FeatureKeyword {
    tech_feature: string;
    translation: string;
    synonym: string[];
    synonym_score: number[];
    words: {
        synonym: string[];
        synonym_score: number[];
        tech_feature: string;
        translation: string;
    }[];
}

export interface ComparisonFeature {
    comparison_feature: string;
    public_score: number;
    score: number;
    tech_feature: string;
    similar: boolean;
    type: TechFeatureType;
    /**
     * 模型输出的原始特征类型文本
     */
    original_type: string;
    reason: string;
    /**
     * 模型输出的原始特征类型原因
     */
    original_reason: string;
}

export interface FinalResult {
    feature_match_num: number;
    comparative_literature_type: 'X' | 'Y' | 'A';
    features: ComparisonFeature[];
    patent_id: string;
    public_score: number;
    selected: boolean;
    most_similar: boolean;
    user_added: boolean;
    /** 提取特征所用到的语言 */
    lang: string;
}

export interface SearchStrategy<T, K = {}> {
    description_en: string;
    description_zh: string;
    type: T;
    parameters: K;
    query: string;
    try_nty: number;
}

export interface BaseSearchResult<T = {}> {
    comparison_patent_num: number;
    comparison_result: ({
        feature_match_num: number;
        patent_id: string;
        public_score: number;
    } & T)[];
    cost: number;
    find_patent_num: number;
    status: 'running' | 'success';
}

export interface SemanticSearch extends BaseSearchResult<{
    score: number;
}> {
    strategy: SearchStrategy<'semantic_search', {
        features: string[];
    }>;
}

export interface IpcCpcSearch extends BaseSearchResult {
    strategy: SearchStrategy<'ipc_cpc_search', {
        cpc: string[];
        ipc: string[];
    }>;
}

export interface BlockProgressSearch extends BaseSearchResult {
    strategy: SearchStrategy<'block_progressive_search'>;
}

export interface RelevantTrackingSearch extends BaseSearchResult {
    strategy: SearchStrategy<'relevant_tracking_search'>;
}

export interface SearchNoveltyResultResponse {
    feature_keywords: FeatureKeyword[];
    final_result: FinalResult[];
    find_patent_num: number;
    is_async: boolean;
    process_dict: {
        ipc_cpc_search?: IpcCpcSearch;
        semantic_search?: SemanticSearch;
        block_progressive_search_1?: BlockProgressSearch;
        block_progressive_search_2?: BlockProgressSearch;
        block_progressive_search_3?: BlockProgressSearch;
        block_progressive_search_4?: BlockProgressSearch;
        relevant_tracking_search?: RelevantTrackingSearch;
    };
    /**
     * 检索耗时，单位：秒
     */
    task_cost: number;
    task_id: string;
    task_status: 'running' | 'success' | 'canceled' | 'failed';
    total_comparison_patent_num: number;
    update_ts: string;
    /**
     * 图片转文本结果（可选）
     * 当用户上传图片进行识别时，此字段包含识别结果
     */
    image_to_text_result?: ImageToTextResult;
}
export type SearchNoveltyResultResponseV2 = RetrievalProgressStateV2

/**
 * 新颖性检索结果获取 API
 */
export interface SearchNoveltyResultApi {
    /**
     * 获取新颖性检索结果
     * @param params 请求参数
     * @param signal abort 信号
     * @returns 新颖性检索结果
     */
    getSearchNoveltyResult(params: SearchNoveltyResultRequest, signal?: AbortSignal): Promise<SearchNoveltyResultResponseV2>;
}

export const SearchNoveltyResultApi = Symbol.for('SearchNoveltyResultApi');
